import {
  Modal,
  Paper,
  IconButton,
  Box,
  Typography,
  Rating,
  TextField,
  Button,
  Fade,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useState, useEffect } from "react";

const emojiMap = {
  1: { emoji: "😡", label: "Angry" },
  2: { emoji: "😕", label: "Disappointed" },
  3: { emoji: "😐", label: "Neutral" },
  4: { emoji: "😊", label: "Satisfied" },
  5: { emoji: "😍", label: "Love it" },
};

function ReviewModal({ open, orderId, productName, productImage, initialRating = 0, initialReview = "", onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Sync state when modal opens or initials change
  useEffect(() => {
    if (open) {
      setRating(initialRating);
      setReview(initialReview);
      setSubmitted(false);
    }
  }, [open, initialRating, initialReview]);

  const handleRatingChange = (event, newValue) => {
    if (newValue !== null) {
      setRating(newValue);
    }
  };

  const handleSubmit = async () => {
    if (loading || rating === 0) return;

    setLoading(true);
    try {
      await onSubmit({
        rating,
        review,
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        backdropFilter: "blur(4px)",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 450,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          position: "relative",
          bgcolor: "background.paper",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
            "&:hover": { color: "error.main" },
          }}
        >
          <Close />
        </IconButton>

        {!submitted ? (
          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ mb: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
               <Avatar src={productImage} variant="rounded" sx={{ width: 80, height: 80, border: "1px solid #eee" }} />
               <Typography variant="subtitle1" fontWeight={700} sx={{ maxWidth: "80%", lineHeight: 1.2 }}>
                  {productName}
               </Typography>
            </Box>

            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 1 }}>
              {initialRating > 0 ? "Edit Your Review" : "Rate Your Experience"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Your feedback helps us improve our service.
            </Typography>

            {/* Star Rating */}
            <Rating
              value={rating}
              onChange={handleRatingChange}
              size="large"
              sx={{
                fontSize: "3rem",
                mb: 2,
                "& .MuiRating-iconFilled": {
                  color: "#FFD700",
                },
              }}
            />

            {/* Emoji Rating */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mb: 4,
                transition: "all 0.3s ease",
              }}
            >
              {[1, 2, 3, 4, 5].map((val) => (
                <Box
                  key={val}
                  onClick={() => setRating(val)}
                  sx={{
                    fontSize: "2rem",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    transform: rating === val ? "scale(1.3)" : "scale(1)",
                    opacity: rating === 0 || rating === val ? 1 : 0.3,
                    filter: rating !== 0 && rating !== val ? "grayscale(40%)" : "none",
                    "&:hover": {
                      transform: "scale(1.2)",
                      opacity: 1,
                    },
                  }}
                >
                  {emojiMap[val].emoji}
                </Box>
              ))}
            </Box>

            {/* Review Input */}
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Write your review here... (optional)"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              disabled={rating === 0 || loading}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 3,
                  bgcolor: rating === 0 ? "rgba(0,0,0,0.02)" : "transparent",
                  transition: "all 0.3s ease",
                },
              }}
            />

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              disabled={rating === 0 || loading}
              onClick={handleSubmit}
              sx={{
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 500,
                boxShadow: "none",
                bgcolor: "black",
                color: "white",
                "&:hover": {
                  bgcolor: "#333",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
                "&:disabled": {
                  bgcolor: "grey.200",
                  color: "grey.400",
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  <span>Submitting...</span>
                </Box>
              ) : (
                "Submit Review"
              )}
            </Button>
          </Box>
        ) : (
          <Fade in={submitted}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Box
                sx={{
                  fontSize: "6rem",
                  mb: 2,
                  animation: "bounceIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
                  "@keyframes bounceIn": {
                    "0%": { transform: "scale(0)" },
                    "50%": { transform: "scale(1.2)" },
                    "100%": { transform: "scale(1)" },
                  },
                }}
              >
                {emojiMap[rating].emoji}
              </Box>
              <Typography variant="h4" fontWeight={800} gutterBottom>
                Thank You!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Thank you for your review!
              </Typography>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  textTransform: "none",
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "text.primary",
                    bgcolor: "grey.50",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Fade>
        )}
      </Paper>
    </Modal>
  );
}

export default ReviewModal;
